import {useEffect, useState} from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Stack,
  Divider,
  IconButton,
  TextFieldVariants,
  Theme,
  SxProps,
} from "@mui/material";
import {findAsset, translate} from "@util";
import {AssetRow} from "./AssetRow";
import {Asset} from "@core/model";
import {CancelIcon} from "@components/icons";
import {useSelector} from "react-redux";
import {Selectors} from "@core/store/selectors";

interface AssetSelectorProps {
  value: string[] | undefined;
  variant?: TextFieldVariants;
  label?: string;
  disabled?: boolean;
  sx?: SxProps<Theme>;
  selectionMode?: "single" | "multiple";
  onChange: (asset: string[] | undefined) => void;
}

const AssetSelector = ({
  value,
  variant,
  disabled,
  label = translate("entity.ticket.asset"),
  sx,
  selectionMode = "single",
  onChange,
}: AssetSelectorProps) => {
  const [open, setOpen] = useState(false);
  const [selection, setSelection] = useState<string[]>([]);

  const assets = useSelector(Selectors.assets);

  useEffect(() => {
    setSelection(value ?? []);
  }, [value]);

  const handleSelect = (assetId: string) => {
    if (selectionMode === "single") {
      setSelection([assetId]);
    } else {
      setSelection(old => {
        if (old?.includes(assetId)) {
          return old.filter(a => a !== assetId);
        } else {
          return [...(old ?? []), assetId];
        }
      });
    }
  };

  const handleConfirm = () => {
    onChange(selection!);
    setOpen(false);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const renderValue = () => {
    if (!value || value.length === 0) return " ";
    const selectedAssets = value
      .map(s => findAsset(assets ?? [], s))
      .filter(a => a) as Asset[];
    return selectedAssets.map(a => a.name).join(", ");
  };

  return (
    <>
      <TextField
        label={label ? translate(label) : undefined}
        variant={variant ?? "standard"}
        value={renderValue()}
        onClick={() => setOpen(true)}
        disabled={disabled}
        slotProps={{
          input: {
            readOnly: true,
            endAdornment: !disabled && selection.length > 0 && (
              <IconButton
                size="small"
                onClick={e => {
                  e.stopPropagation();
                  setSelection([]);
                  onChange(undefined);
                }}>
                <CancelIcon fontSize="inherit" />
              </IconButton>
            ),
          },
        }}
        sx={{cursor: "pointer", ...sx}}
      />
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle textAlign={"center"}>
          {translate("common.action.select")}
        </DialogTitle>
        <DialogContent>
          <Stack
            height={"70vh"}
            divider={<Divider orientation="vertical" flexItem />}>
            {assets?.map(a => (
              <AssetRow
                key={a.id}
                asset={a}
                selection={selection}
                level={0}
                onSelect={handleSelect}
              />
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>
            {translate("common.action.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={selection.length === 0}
            variant="contained">
            {translate("common.action.confirm")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export {AssetSelector};
